import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui';
import Select from 'react-select';
import { optionsPartido, optionsEstado, optionsTipoSubsidio } from '../datos/options';

const optionsTipoPago = [
    { value: 'anticipo', label: 'Anticipo' },
    { value: '2do_pago', label: '2do pago' },
    { value: '3er_pago', label: '3er pago' },
    { value: 'adenda', label: 'Adenda' },
    { value: 'sin_pagos', label: 'Sin pagos realizados' },
];

// Catálogo de criterios. tipo controla cómo se completa en el chat:
//   'select'  → botones de opción con catálogo corto
//   'multi'   → select múltiple (react-select isMulti) dentro del chat
//   'text'    → campo de texto libre
//   'rango'   → un rango (min-máx) y se confirma con un botón
const CRITERIOS = [
    { key: 'partido', titulo: 'Partido', pregunta: 'Elegí uno o más partidos (podés buscarlos) y confirmá:', tipo: 'multi', sugerencia: 'Partido', emoji: '📍', opciones: optionsPartido },
    { key: 'avance', titulo: 'Avance físico', pregunta: '¿Qué rango de avance físico te interesa? (ej: desde 50 hasta 100)', tipo: 'rango', minimo: 'avance_min', maximo: 'avance_max', sugerencia: 'Avance físico', emoji: '📈', etiquetaM: 'Mín %', etiquetaX: 'Máx %', sufijo: '%' },
    { key: 'beneficiarios', titulo: 'Cantidad de beneficiarios', pregunta: '¿Qué rango de beneficiarios te interesa? (ej: desde 100 hasta 500)', tipo: 'rango', minimo: 'beneficiarios_min', maximo: 'beneficiarios_max', sugerencia: 'Beneficiarios', emoji: '👥', etiquetaM: 'Mín', etiquetaX: 'Máx', sufijo: '' },
    { key: 'tipo_pago', titulo: 'Tipo de pago', pregunta: '¿Qué tipo de pago?', tipo: 'select', sugerencia: 'Tipo de pago', emoji: '💸', opciones: optionsTipoPago, mapeo: (v) => v.label, val: (v) => v.value },
    { key: 'monto_obra', titulo: 'Monto de obra', pregunta: '¿Qué rango de monto total de obra? (ej: desde 1000000 hasta 5000000)', tipo: 'rango', minimo: 'monto_obra_min', maximo: 'monto_obra_max', sugerencia: 'Monto de obra', emoji: '💰', etiquetaM: 'Mín $', etiquetaX: 'Máx $' },
    { key: 'monto_pago', titulo: 'Monto de pago', pregunta: '¿Qué rango de monto por pago? (ej: desde 50000 hasta 300000)', tipo: 'rango', minimo: 'monto_min', maximo: 'monto_max', sugerencia: 'Monto de pago', emoji: '💵', etiquetaM: 'Mín $', etiquetaX: 'Máx $' },
    { key: 'estado', titulo: 'Estado', pregunta: '¿En qué estado está la obra?', tipo: 'select', sugerencia: 'Estado', emoji: '🚦', opciones: optionsEstado, mapeo: (v) => v.label, val: (v) => v.value },
    { key: 'tipo_subsidio', titulo: 'Tipo de subsidio', pregunta: '¿Qué tipo de subsidio?', tipo: 'select', sugerencia: 'Tipo de subsidio', emoji: '🏛️', opciones: optionsTipoSubsidio, mapeo: (v) => v.label, val: (v) => v.value },
];

const valoresVacios = {
    partido: [],
    avance_min: '', avance_max: '',
    beneficiarios_min: '', beneficiarios_max: '',
    tipo_pago: null,
    monto_obra_min: '', monto_obra_max: '',
    monto_min: '', monto_max: '',
    estado: null,
    tipo_subsidio: null,
};

const construirParams = (f) => {
    const params = new URLSearchParams();
    const criterios = [];

    if (f.consulta && f.consulta.trim()) params.set('q', f.consulta.trim());

    if (Array.isArray(f.partido) && f.partido.length) {
        f.partido.forEach((p) => params.append('partido', p.label));
        criterios.push(`Partido: ${f.partido.map((p) => p.label).join(', ')}`);
    }

    if (f.avance_min !== '' && f.avance_min != null) { params.set('porc_avance_min', String(f.avance_min)); criterios.push(`Avance ≥ ${f.avance_min}%`); }
    if (f.avance_max !== '' && f.avance_max != null) { params.set('porc_avance_max', String(f.avance_max)); criterios.push(`Avance ≤ ${f.avance_max}%`); }

    if (f.beneficiarios_min !== '' && f.beneficiarios_min != null) { params.set('beneficiarios_min', String(f.beneficiarios_min)); criterios.push(`Beneficiarios ≥ ${f.beneficiarios_min}`); }
    if (f.beneficiarios_max !== '' && f.beneficiarios_max != null) { params.set('beneficiarios_max', String(f.beneficiarios_max)); criterios.push(`Beneficiarios ≤ ${f.beneficiarios_max}`); }

    if (f.monto_min !== '' && f.monto_min != null) { params.set('monto_min', String(f.monto_min)); criterios.push(`Pago ≥ $${f.monto_min}`); }
    if (f.monto_max !== '' && f.monto_max != null) { params.set('monto_max', String(f.monto_max)); criterios.push(`Pago ≤ $${f.monto_max}`); }

    if (f.monto_obra_min !== '' && f.monto_obra_min != null) { params.set('monto_obra_min', String(f.monto_obra_min)); criterios.push(`Monto obra ≥ $${f.monto_obra_min}`); }
    if (f.monto_obra_max !== '' && f.monto_obra_max != null) { params.set('monto_obra_max', String(f.monto_obra_max)); criterios.push(`Monto obra ≤ $${f.monto_obra_max}`); }

    const tipoPago = f.tipo_pago?.value;
    if (tipoPago) { params.set('tipo_pago', tipoPago); criterios.push(`Tipo de pago: ${f.tipo_pago.label}`); }

    const estado = f.estado?.value;
    if (estado) { params.set('estado', estado); criterios.push(`Estado: ${estado}`); }

    const tipoSubsidio = f.tipo_subsidio?.value;
    if (tipoSubsidio) { params.set('tipo_subsidio', tipoSubsidio); criterios.push(`Tipo subsidio: ${tipoSubsidio}`); }

    return { params, criterios };
};

// Detección por reglas: ¿qué criterio matchea el texto libre?
const SINONIMOS = {
    partido: ['partido', 'municipio', 'moreno', 'general rodríguez', 'la matanza', 'merlo', 'morón', 'iturbe', 'trujui'],
    avance: ['avance', 'porcentaje', 'adelanto', '%', 'avanzado', 'avanzada'],
    beneficiarios: ['beneficiario', 'beneficiarios', 'gente', 'personas', 'poblacion'],
    tipo_pago: ['pago', 'anticipo', 'adenda', 'cuota'],
    monto_obra: ['monto de obra', 'presupuesto', 'costo', 'monto total'],
    monto_pago: ['monto de pago', 'importe', 'monto'],
    estado: ['estado', 'situacion', 'en curso', 'finalizado', 'finalizada', 'pendiente', 'paralizada', 'en licitacion'],
    tipo_subsidio: ['subsidio', 'financiamiento', 'municipio', 'ong', 'universidad'],
};

const detectarCriterio = (texto) => {
    const t = ` ${texto.toLowerCase()} `;
    for (const [key, sinos] of Object.entries(SINONIMOS)) {
        for (const s of sinos) {
            if (t.includes(` ${s} `) || t.includes(s + ' ') || t.includes(' ' + s)) return key;
        }
    }
    return null;
};

// ---- Detección multi-criterio (replica la semántica del intérprete del backend) ----

const normalizarTB = (t) => (t || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const limpiarNum = (s) => {
    const n = Number(String(s || '').replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

// Barre el catálogo de partidos y devuelve todos los que aparecen en el texto.
const detectarPartidos = (texto) => {
    const tn = ' ' + normalizarTB(texto) + ' ';
    return optionsPartido.filter((o) => {
        const l = normalizarTB(o.label);
        if (!l) return false;
        const enc = ' ' + l + ' ';
        if (tn.includes(enc)) return true;
        // Límites con puntuación normalizada (dígitos pegados no).
        return (tn.startsWith(l + ' ') || tn.endsWith(' ' + l) || tn === l);
    });
};

const detectarEstado = (texto) => {
    const tn = normalizarTB(texto);
    const pares = [
        [/finalizad|terminad/, 'Finalizado'],
        [/paralizad|suspendid|inactivo,? ?o ?inactivo/, 'Observado, inactivo o suspendido'],
        [/observad/, 'Observado, inactivo o suspendido'],
        [/analsis|analisis/, 'Análisis preliminar'],
        [/guarda/, 'En guarda'],
        [/rescisio/, 'Rescisión'],
        [/circuito adminis/, 'En circuito administrativo'],
        [/seguimiento/, 'Seguimiento'],
    ];
    const byLabel = (l) => optionsEstado.find((o) => normalizarTB(o.label) === normalizarTB(l));
    for (const [re, label] of pares) {
        if (re.test(tn)) return byLabel(label);
    }
    return null;
};

const detectarTipoSubsidio = (texto) => {
    const tn = normalizarTB(texto);
    const byLabel = (l) => optionsTipoSubsidio.find((o) => normalizarTB(o.label) === normalizarTB(l));
    if (/municipio/.test(tn)) return byLabel('Subsidio a municipio');
    if (/ong|no gubernamental|asociacion|organizacion/.test(tn)) return byLabel('Subsidio a ONG');
    if (/universidad|universidades/.test(tn)) return byLabel('Subsidio a Universidades');
    return null;
};

const detectarTipoPago = (texto) => {
    const tn = normalizarTB(texto);
    if (/sin pagos|sin pago|no (?:tiene|tienen|hayan|hubo|hay) pagos|pagos sin/.test(tn)) return optionsTipoPago.find((o) => o.value === 'sin_pagos');
    if (/anticipo/.test(tn)) return optionsTipoPago.find((o) => o.value === 'anticipo');
    if (/(?:2do|segundo) pago/.test(tn)) return optionsTipoPago.find((o) => o.value === '2do_pago');
    if (/(?:3er|tercero|tercer) pago/.test(tn)) return optionsTipoPago.find((o) => o.value === '3er_pago');
    if (/adenda/.test(tn)) return optionsTipoPago.find((o) => o.value === 'adenda');
    return null;
};

// Avance físico: números y palabras cualitativas ("alto", "bajo", "medio").
const detectarAvance = (texto) => {
    const tn = normalizarTB(texto);
    const hayAvance = /(avance|progreso|porcentaje de|% de avance|% )/.test(tn);
    if (!hayAvance) return null;

    const entre = tn.match(/(?:entre)\s+(\d[\d.,]*)\s*(?:y|a)\s*(\d[\d.,]*)/);
    if (entre) {
        const a = limpiarNum(entre[1]);
        const b = limpiarNum(entre[2]);
        if (a != null && b != null) return { min: Math.min(a, b), max: Math.max(a, b) };
    }
    const mayor = tn.match(/(?:mayor|superior|arriba|mas|min)\s*(?:al|a|de|del|que|igual)?\s*(\d[\d.,]*)/);
    if (mayor) {
        const n = limpiarNum(mayor[1]);
        if (n != null) return { min: n, max: null };
    }
    const menor = tn.match(/(?:menor|menos|inferior|bajo|max)\s*(?:al|a|de|del|que|igual)?\s*(\d[\d.,]*)/);
    if (menor) {
        const n = limpiarNum(menor[1]);
        if (n != null) return { min: null, max: n };
    }
    if (/(alto|avanzad)/.test(tn)) return { min: 70, max: null };
    if (/(medio|regular)/.test(tn)) return { min: 30, max: 70 };
    if (/(bajo|escaso|poco|inicial|inicio)/.test(tn)) return { min: null, max: 30 };
    return null;
};

// Montos en texto libre: obra o pago según la palabra que acompañe.
const detectarMontos = (texto) => {
    const tn = normalizarTB(texto);
    const esObra = /(monto de obra|monto total|presupuesto|valor de obra)/.test(tn);
    const base = esObra ? 'monto_obra' : 'monto_pago';
    let min = null, max = null;
    const entre = tn.match(/(?:entre)\s+(\d[\d.,]*)\s*(?:y|a)\s*(\d[\d.,]*)/);
    if (entre) { min = limpiarNum(entre[1]); max = limpiarNum(entre[2]); }
    else {
        const mayor = tn.match(/(?:mayor|superior|mas|arriba|mas de)\s*(?:al|a|de|del|que|igual)?\s*(\d[\d.,]*)/);
        if (mayor) min = limpiarNum(mayor[1]);
        else {
            const menor = tn.match(/(?:menor|menos|inferior|bajo)\s*(?:al|a|de|del|que|igual)?\s*(\d[\d.,]*)/);
            if (menor) max = limpiarNum(menor[1]);
        }
    }
    if (min == null && max == null) return null;
    // si no se menciona "monto/pago/presupuesto", evitar falsos positivos con avance
    if (!/(monto|pago|presupuesto|valor|importe|costo)/.test(tn)) return null;
    return { base, min, max };
};

// Texto legible de un valor para mostrarlo como chip del usuario.
const textoValor = (key, f) => {
    switch (key) {
        case 'partido': return Array.isArray(f.partido) && f.partido.length ? f.partido.map((p) => p.label).join(', ') : '';
        case 'avance': {
            const partes = [];
            if (f.avance_min !== '') partes.push(`≥ ${f.avance_min}%`);
            if (f.avance_max !== '') partes.push(`≤ ${f.avance_max}%`);
            return partes.join(' y ');
        }
        case 'beneficiarios': {
            const partes = [];
            if (f.beneficiarios_min !== '') partes.push(`≥ ${f.beneficiarios_min}`);
            if (f.beneficiarios_max !== '') partes.push(`≤ ${f.beneficiarios_max}`);
            return partes.join(' y ');
        }
        case 'tipo_pago': return f.tipo_pago?.label;
        case 'monto_obra': {
            const partes = [];
            if (f.monto_obra_min !== '') partes.push(`≥ $${f.monto_obra_min}`);
            if (f.monto_obra_max !== '') partes.push(`≤ $${f.monto_obra_max}`);
            return partes.join(' y ');
        }
        case 'monto_pago': {
            const partes = [];
            if (f.monto_min !== '') partes.push(`≥ $${f.monto_min}`);
            if (f.monto_max !== '') partes.push(`≤ $${f.monto_max}`);
            return partes.join(' y ');
        }
        case 'estado': return f.estado?.label;
        case 'tipo_subsidio': return f.tipo_subsidio?.label;
        default: return '';
    }
};

// Mensaje inicial del bot.
const saludo = 'Hola 👋. Contame qué buscás — por ejemplo "obras de Moreno con avance alto", o tocá un botón para ir armando tu búsqueda paso a paso.';

const BusquedaAvanzadaModal = ({ isOpen, onClose, consultaInicial = '', onBuscar }) => {
    const [f, setF] = useState(() => ({ consulta: consultaInicial, ...valoresVacios }));
    // mensajes: { rol: 'bot'|'user', texto, opciones?, esperando?: key (qué campo se espera) }
    const [mensajes, setMensajes] = useState([]);
    const [pendiente, setPendiente] = useState(null); // criterio actualmente a completar
    const [inputTexto, setInputTexto] = useState('');
    const finRef = useRef(null);

    const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

    const inicializar = () => {
        setF({ consulta: consultaInicial, ...valoresVacios });
        setMensajes([{ rol: 'bot', texto: saludo, opciones: CRITERIOS.map((c) => ({ label: `${c.emoji} ${c.titulo}`, valor: c.key, accion: 'criterio' })) }]);
        setPendiente(null);
        setInputTexto('');
    };

    useEffect(() => {
        if (isOpen) inicializar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, [mensajes, pendiente]);

    if (!isOpen) return null;

    // Pregunta según el tipo de criterio.
    const preguntar = (key) => {
        const c = CRITERIOS.find((x) => x.key === key);
        if (!c) return;
        if (c.tipo === 'select') {
            setMensajes((prev) => [...prev, {
                rol: 'bot',
                texto: c.pregunta,
                esperando: key,
                opciones: c.opciones.map((o) => ({ label: o.label, valor: o, valorPerfil: c.mapeo(o), accion: 'opcion' })),
            }]);
        } else if (c.tipo === 'multi') {
            setMensajes((prev) => [...prev, { rol: 'bot', texto: c.pregunta, esperando: key, esMulti: true }]);
        } else if (c.tipo === 'text') {
            setMensajes((prev) => [...prev, { rol: 'bot', texto: c.pregunta, esperando: key }]);
        } else {
            // rango: usuario completa min y máx en la burbuja de inputs.
            setMensajes((prev) => [...prev, { rol: 'bot', texto: c.pregunta, esperando: key, esRango: true }]);
        }
    };

    // Agrega la burbuja del usuario.
    const registrar = (textoUser) => {
        setMensajes((prev) => [...prev, { rol: 'user', texto: textoUser }]);
    };

    const resolverOpcion = (criterioEsperado, opcion) => {
        if (opcion.accion === 'criterio') {
            // El usuario quiere profundizar ese criterio; el criterio está en opcion.valor.
            const key = opcion.valor;
            const cr = CRITERIOS.find((c) => c.key === key);
            if (!cr) return;
            setF((prev) => ({ ...prev, consulta: '' }));
            registrar(cr.emoji + ' ' + cr.titulo);
            setPendiente(key);
            preguntar(key);
            return;
        }
        if (opcion.accion === 'terminar') {
            buscar();
            return;
        }
        // accion 'opcion': asignar valor select (criterioEsperado).
        const key = criterioEsperado;
        if (key && (key === 'tipo_pago' || key === 'estado' || key === 'tipo_subsidio')) {
            setF((prev) => ({ ...prev, [key]: opcion.valor }));
            registrar(opcion.valorPerfil);
        }
        setPendiente(null);
        setMensajes((prev) => [...prev, confirmarSiguiente()]);
    };

    const confirmarSiguiente = () => {
        const restantes = CRITERIOS.filter((c) => !textoValor(c.key, f));
        if (restantes.length === 0) {
            return { rol: 'bot', texto: '¡Listo! Tengo toda la info para buscar. 🎯 Usá el botón verde inferior para ejecutar la búsqueda.' };
        }
        const siguiente = restantes[0];
        return {
            rol: 'bot',
            texto: `✔ Anotado. ¿Querés afinar ${siguiente.emoji} ${siguiente.titulo.toLowerCase()} o ya buscamos?`,
            opciones: [
                { label: `${siguiente.emoji} ${siguiente.titulo}`, valor: siguiente.key, accion: 'criterio' },
                ...restantes.slice(1).map((c) => ({ label: `${c.emoji} ${c.titulo}`, valor: c.key, accion: 'criterio' })),
            ],
        };
    };

    // Confirmar criterio tipo 'multi' (partido con select múltiple).
    const confirmarMulti = (key) => {
        const texto = textoValor(key, f);
        if (!texto) return;
        registrar(texto);
        setPendiente(null);
        setMensajes((prev) => [...prev, confirmarSiguiente()]);
    };

    // Respuesta del usuario por input de texto.
    const responderTexto = (key, texto) => {
        registrar(texto);
        const c = CRITERIOS.find((x) => x.key === key);
        if (c && c.tipo === 'select') {
            // Intenta matchear el texto contra las opciones por etiqueta.
            const t = texto.trim().toLowerCase();
            const match = c.opciones.find((o) => o.label.toLowerCase() === t || o.label.toLowerCase().includes(t) || t.includes(o.label.toLowerCase()));
            if (match) {
                setF((prev) => ({ ...prev, [key]: match }));
                setPendiente(null);
                setMensajes((prev) => [...prev, confirmarSiguiente()]);
            } else {
                setMensajes((prev) => [...prev, { rol: 'bot', texto: `No encontré "${texto}". Elegí una de las opciones.`, esperando: key, opciones: c.opciones.map((o) => ({ label: o.label, valor: o, valorPerfil: c.mapeo(o), accion: 'opcion' })) }]);
            }
        } else if (key === 'avance' || key === 'monto_obra' || key === 'monto_pago' || key === 'beneficiarios') {
            const c = CRITERIOS.find((x) => x.key === key);
            const quedaTomado = (k, min, max) => {
                setF((prev) => ({ ...prev, [c.minimo]: min, [c.maximo]: max }));
                if (min !== '' || max !== '') {
                    setPendiente(null);
                    setMensajes((prev) => [...prev, confirmarSiguiente()]);
                } else {
                    setMensajes((prev) => [...prev, { rol: 'bot', texto: 'No entendí el rango. Escribí por ejemplo "desde 50 hasta 80".', esperando: key }]);
                }
            };
            const mMin = texto.match(/(?:desde|de|mayor|mas|más|min|>)\s*[$]?\s*([\d.,]+)/i);
            const mMax = texto.match(/(?:hasta|a|menor|menos|max|máx|<)\s*[$]?\s*([\d.,]+)/i);
            const limpiar = (s) => (s || '').replace(/,/g, '.').trim();
            let min = mMin ? limpiar(mMin[1]) : '';
            let max = mMax ? limpiar(mMax[1]) : '';
            if (min === '' && max === '' && /^\d+([.,]\d+)?$/.test(texto.trim())) {
                min = limpiar(texto.trim());
            }
            quedaTomado(key, min, max);
        }
    };

    const buscar = () => {
        const { params, criterios } = construirParams(f);
        if (!criterios.length && !f.consulta.trim()) return;
        onBuscar(params, criterios.join(' | '));
    };

    // Intenta aplicar un criterio directamente desde el texto libre, sin mostrar opciones.
    // Devuelve true si pudo asignar el valor (y el valor se registró en el chat).
    const aplicarCriterioDesdeTexto = (key, texto) => {
        const c = CRITERIOS.find((x) => x.key === key);
        if (!c) return false;
        const t = texto.toLowerCase();

        if (c.tipo === 'multi') {
            const found = c.opciones.filter((o) => t.includes(o.label.toLowerCase()));
            if (!found.length) return false;
            setF((prev) => ({ ...prev, [key]: found, consulta: '' }));
            registrar(c.emoji + ' ' + found.map((o) => o.label).join(', '));
            setPendiente(null);
            setMensajes((prev) => [...prev, confirmarSiguiente()]);
            return true;
        }

        if (c.tipo === 'select') {
            const found = c.opciones.find((o) => t.includes(o.label.toLowerCase()));
            if (!found) return false;
            setF((prev) => ({ ...prev, [key]: found, consulta: '' }));
            registrar(c.emoji + ' ' + (c.mapeo ? c.mapeo(found) : found.label));
            setPendiente(null);
            setMensajes((prev) => [...prev, confirmarSiguiente()]);
            return true;
        }

        if (c.tipo === 'rango') {
            const mMin = texto.match(/(?:desde|de|mayor|mas|más|min|>)\s*[$]?\s*([\d.,]+)/i);
            const mMax = texto.match(/(?:hasta|a|menor|menos|max|máx|<)\s*[$]?\s*([\d.,]+)/i);
            const limpiar = (s) => (s || '').replace(/,/g, '.').trim();
            let min = mMin ? limpiar(mMin[1]) : '';
            let max = mMax ? limpiar(mMax[1]) : '';
            if (min === '' && max === '' && /^\d+([.,]\d+)?$/.test(texto.trim())) min = limpiar(texto.trim());
            if (min === '' && max === '') return false;
            setF((prev) => ({ ...prev, consulta: '', [c.minimo]: min, [c.maximo]: max }));
            const partes = [];
            const sufijo = c.sufijo ?? (c.key === 'avance' ? '%' : '$');
            if (min !== '') partes.push(`≥ ${min}${sufijo}`);
            if (max !== '') partes.push(`≤ ${max}${sufijo}`);
            registrar(c.emoji + ' ' + partes.join(' y '));
            setPendiente(null);
            setMensajes((prev) => [...prev, confirmarSiguiente()]);
            return true;
        }

        return false;
    };

    const enviarTexto = () => {
        const texto = inputTexto.trim();
        if (!texto) return;
        // Si hay un criterio pendiente, ese texto es su valor.
        if (pendiente) {
            responderTexto(pendiente, texto);
            setInputTexto('');
            return;
        }
        // Si no hay pendiente: interpretamos el texto y aplicamos TODOS los criterios reconocibles a la vez
        // (partido, estado, tipo de subsidio, tipo de pago, avance físico con cualitativos, montos).
        const partidos = detectarPartidos(texto);
        const estado = detectarEstado(texto);
        const tipoSubsidio = detectarTipoSubsidio(texto);
        const tipoPago = detectarTipoPago(texto);
        const avance = detectarAvance(texto);
        const montos = detectarMontos(texto);
        const hayCriterio = partidos.length || estado || tipoSubsidio || tipoPago || avance || montos;

        if (!hayCriterio) {
            setF((prev) => ({ ...prev, consulta: texto }));
            registrar(texto);
            setMensajes((prev) => [...prev, {
                rol: 'bot',
                texto: 'Tomé nota de eso como texto libre. ¿Querés agregar algún filtro para afinar?',
                opciones: [...CRITERIOS.map((c) => ({ label: `${c.emoji} ${c.titulo}`, valor: c.key, accion: 'criterio' }))],
            }]);
            setInputTexto('');
            return;
        }

        // Construir proyección del estado para chips y para saber qué criterios quedan.
        let proyectado = { ...f };
        const chips = [];
        if (partidos.length) {
            const actuales = Array.isArray(proyectado.partido) ? proyectado.partido : [];
            const nuevos = [...actuales];
            partidos.forEach((p) => { if (!nuevos.some((x) => x.value === p.value)) nuevos.push(p); });
            proyectado = { ...proyectado, partido: nuevos };
            chips.push('📍 ' + partidos.map((p) => p.label).join(', '));
        }
        if (estado) { proyectado = { ...proyectado, estado }; chips.push('🚦 ' + estado.label); }
        if (tipoSubsidio) { proyectado = { ...proyectado, tipo_subsidio: tipoSubsidio }; chips.push('🏛️ ' + tipoSubsidio.label); }
        if (tipoPago) { proyectado = { ...proyectado, tipo_pago: tipoPago }; chips.push('💸 ' + tipoPago.label); }
        if (avance) {
            proyectado = { ...proyectado, avance_min: avance.min != null ? String(avance.min) : '', avance_max: avance.max != null ? String(avance.max) : '' };
            chips.push('📈 Avance ' + (avance.min != null ? `≥ ${avance.min}` : '') + ((avance.min != null && avance.max != null) ? ' y ' : '') + (avance.max != null ? `≤ ${avance.max}` : '') + '%');
        }
        if (montos) {
            const keyMin = montos.base === 'monto_obra' ? 'monto_obra_min' : 'monto_min';
            const keyMax = montos.base === 'monto_obra' ? 'monto_obra_max' : 'monto_max';
            proyectado = { ...proyectado, [keyMin]: montos.min != null ? String(montos.min) : '', [keyMax]: montos.max != null ? String(montos.max) : '' };
            chips.push((montos.base === 'monto_obra' ? '💰' : '💵') + (montos.min != null ? ` ≥ $${montos.min}` : '') + (montos.min != null && montos.max != null ? ' y' : '') + (montos.max != null ? ` ≤ $${montos.max}` : ''));
        }

        // Consulta semántica: quitar del texto las entidades consumidas y el relleno.
        let resto = ' ' + normalizarTB(texto) + ' ';
        const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        partidos.forEach((p) => { resto = resto.split(esc(normalizarTB(p.label))).join(' '); });
        if (estado) resto = resto.split(esc(normalizarTB(estado.label))).join(' ');
        if (tipoSubsidio) resto = resto.split(esc(normalizarTB(tipoSubsidio.label))).join(' ');
        if (tipoPago) resto = resto.split(esc(normalizarTB(tipoPago.label))).join(' ');
        if (avance) resto = resto.split('avance').join(' ');
        resto = resto.replace(/\b(obras|obra|de|en|con|que|los|las|un|una|del|al|y|para|por|alto|baja|bajo|medio)\b/g, ' ').replace(/\s+/g, ' ').trim();

        proyectado = { ...proyectado, consulta: resto };

        setF(proyectado);
        registrar(texto);
        chips.forEach((c) => registrar(c));

        const restantesProy = CRITERIOS.filter((c) => !textoValor(c.key, proyectado));
        if (restantesProy.length === 0) {
            setMensajes((prev) => [...prev, { rol: 'bot', texto: '¡Listo! Detecté todos los criterios. 🎯 Usá el botón verde inferior para ejecutar la búsqueda.' }]);
        } else {
            const siguiente = restantesProy[0];
            setMensajes((prev) => [...prev, {
                rol: 'bot',
                texto: `✔ Apliqué: ${chips.join(' · ')}. ¿Querés afinar ${siguiente.emoji} ${siguiente.titulo.toLowerCase()} o ya buscamos?`,
                opciones: restantesProy.map((c) => ({ label: `${c.emoji} ${c.titulo}`, valor: c.key, accion: 'criterio' })),
            }]);
        }
        setInputTexto('');
    };

    const confirmarRango = (key) => {
        const c = CRITERIOS.find((x) => x.key === key);
        const texto = textoValor(key, f);
        if (f[c.minimo] === '' && f[c.maximo] === '') return;
        registrar(texto);
        setPendiente(null);
        setMensajes((prev) => [...prev, confirmarSiguiente()]);
    };

    const { criterios } = construirParams(f);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Búsqueda asistida</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Conversá conmigo para armar tu búsqueda</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={inicializar} className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">Reiniciar</button>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">×</button>
                    </div>
                </div>

                {criterios.length > 0 && (
                    <div className="px-5 pt-3 flex flex-wrap gap-2">
                        {criterios.map((c) => (
                            <span key={c} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded-full">✔ {c}</span>
                        ))}
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[300px] max-h-[62vh]">
                    {mensajes.map((m, i) => (
                        <Mensaje key={i} m={m} pendiente={pendiente} f={f}
                            onOpcion={resolverOpcion} onInputRango={set} onConfirmarRango={confirmarRango}
                            onConfirmarMulti={confirmarMulti} />                    ))}
                    <div ref={finRef} />
                </div>

                <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                    <div className="flex gap-2">
                        <Input
                            type="text"
                            value={inputTexto}
                            onChange={(e) => setInputTexto(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && enviarTexto()}
                            placeholder="Escribí tu respuesta o lo que querés buscar…"
                        />
                        <button type="button" onClick={enviarTexto} className="px-4 py-2 rounded-md text-sm font-medium bg-blue-500 text-white hover:bg-blue-600">Enviar</button>
                    </div>
                    <button
                        type="button"
                        onClick={buscar}
                        disabled={criterios.length === 0}
                        className={`w-full px-4 py-3 rounded-lg text-sm font-bold transition-colors shadow-lg ${
                            criterios.length > 0
                                ? 'bg-green-500 text-white hover:bg-green-600 shadow-green-500/40 cursor-pointer'
                                : 'bg-gray-300 text-gray-600 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                        }`}
                    >
                        Buscar ahora 🔎
                    </button>
                </div>
            </div>
        </div>
    );
};

// Burbuja de conversación.
const Mensaje = ({ m, pendiente, f, onOpcion, onInputRango, onConfirmarRango, onConfirmarMulti }) => {
    const esBot = m.rol === 'bot';
    const esRango = esBot && m.esRango && pendiente === m.esperando;
    const esMulti = esBot && m.esMulti && pendiente === m.esperando;

    return (
        <div className={`flex ${esBot ? 'justify-start' : 'justify-end'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${esBot ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm' : 'bg-blue-500 text-white rounded-br-sm'}`}>
                <p className="whitespace-pre-wrap">{m.texto}</p>

                {esBot && m.opciones && m.opciones.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {m.opciones.map((o, j) => (
                            <button
                                key={j}
                                type="button"
                                onClick={() => onOpcion(m.esperando, o)}
                                className="px-3 py-1 text-xs rounded-full border border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/40 bg-white dark:bg-gray-800"
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                )}

                {esMulti && (
                    <div className="mt-2">
                        <Select
                            isMulti
                            options={optionsPartido}
                            value={Array.isArray(f.partido) ? f.partido : []}
                            onChange={(val) => onInputRango('partido')(val || [])}
                            placeholder="Buscar/elegir partidos…"
                            className="text-gray-900 dark:text-gray-100"
                        />
                        <button
                            type="button"
                            onClick={() => onConfirmarMulti('partido')}
                            disabled={!Array.isArray(f.partido) || f.partido.length === 0}
                            className="mt-2 px-3 py-1 text-xs rounded-md bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Confirmar selección
                        </button>
                    </div>
                )}

                {esRango && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input type="number" min="0" max={pendiente === 'avance' ? '100' : ''} placeholder={CRITERIOS.find((c) => c.key === pendiente).etiquetaM}
                            value={f[CRITERIOS.find((c) => c.key === pendiente).minimo]}
                            onChange={(e) => onInputRango(CRITERIOS.find((c) => c.key === pendiente).minimo)(e.target.value)}
                            className="w-28 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                        <span className="text-gray-400">-</span>
                        <input type="number" min="0" max={pendiente === 'avance' ? '100' : ''} placeholder={CRITERIOS.find((c) => c.key === pendiente).etiquetaX}
                            value={f[CRITERIOS.find((c) => c.key === pendiente).maximo]}
                            onChange={(e) => onInputRango(CRITERIOS.find((c) => c.key === pendiente).maximo)(e.target.value)}
                            className="w-28 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
                        <button type="button" onClick={() => onConfirmarRango(pendiente)}
                            className="px-3 py-1 text-xs rounded-md bg-blue-500 text-white hover:bg-blue-600">Confirmar</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BusquedaAvanzadaModal;
